import { useEffect, useMemo, useState } from "react";
import { Link, Navigate, useParams, useSearchParams } from "react-router-dom";
import { MapPin, RotateCcw, SearchX, SlidersHorizontal } from "lucide-react";
import Navbar, { MobileActionBar } from "../ui/Navbar";
import Footer from "../ui/Footer";
import UnitCard from "../ui/UnitCard";
import Modal from "../ui/Modal";
import Lightbox from "../ui/Lightbox";
import InquiryForm from "../ui/InquiryForm";
import { CallButton } from "../ui/ContactLinks";
import { getAllUnits, getProjects } from "../firebase/api";
import { useAsync } from "../lib/hooks";
import { areaNumber, compareUnits, floorInfo, unitStatus, unitSummary } from "../lib/format";
import { trackViewContent } from "../lib/pixel";
import { SITE } from "../config/site";
import { useI18n } from "../i18n";
import { useFormat } from "../i18n/format";
import Seo from "../seo/Seo";
import { apartmentComplex, breadcrumbs, unitList } from "../seo/schema";
import { availability } from "../ui/ProjectCard";
import blueprintGif from "../assets/pixel/blueprint.gif";
import "../scss/catalogue.scss";

const AREA_RANGES = [
  { value: "do-40", test: (a) => a < 40 },
  { value: "40-60", test: (a) => a >= 40 && a < 60 },
  { value: "60-80", test: (a) => a >= 60 && a < 80 },
  { value: "80+", test: (a) => a >= 80 },
];

const SORTS = [
  { value: "sprat", fn: compareUnits },
  { value: "povrsina-rastuce", fn: (a, b) => areaNumber(a.povrsina) - areaNumber(b.povrsina) },
  { value: "povrsina-opadajuce", fn: (a, b) => areaNumber(b.povrsina) - areaNumber(a.povrsina) },
];

export default function Catalogue() {
  const { slug } = useParams();
  const { t, lp, lang, field } = useI18n();
  const f = useFormat();
  const [params, setParams] = useSearchParams();
  const projects = useAsync(getProjects, []);
  const units = useAsync(
    () => (projects.data ? getAllUnits(projects.data) : Promise.resolve(null)),
    [projects.data]
  );
  const [inquiryUnit, setInquiryUnit] = useState(null);
  const [gallery, setGallery] = useState(null);
  const [filtersOpen, setFiltersOpen] = useState(false);

  const activeProject = useMemo(
    () => projects.data?.find((p) => p.slug === slug || p.id === slug) || null,
    [projects.data, slug]
  );

  useEffect(() => {
    if (activeProject) trackViewContent(activeProject.identifier);
  }, [activeProject]);

  const filters = {
    tip: params.get("tip") || "",
    struktura: params.get("struktura") || "",
    sprat: params.get("sprat") || "",
    povrsina: params.get("povrsina") || "",
    prodati: params.get("prodati") === "1",
    sort: params.get("sort") || "sprat",
  };

  const setFilter = (key, value) => {
    const next = new URLSearchParams(params);
    if (value === "" || value === false || value === null) next.delete(key);
    else next.set(key, value === true ? "1" : value);
    setParams(next, { replace: true });
  };

  const resetFilters = () => setParams({}, { replace: true });

  const projectById = useMemo(
    () => Object.fromEntries((projects.data || []).map((p) => [p.id, p])),
    [projects.data]
  );

  const scoped = useMemo(
    () => (units.data || []).filter((u) => !activeProject || u.projectId === activeProject.id),
    [units.data, activeProject]
  );

  // Opcije filtera se grade iz stvarnih podataka.
  const options = useMemo(() => {
    const types = [...new Set(scoped.map((u) => u.tip || "stan"))];
    const struct = [...new Set(scoped.filter((u) => (u.tip || "stan") === "stan" && u.struktura).map((u) => String(u.struktura)))]
      .sort((a, b) => parseFloat(a) - parseFloat(b));
    const floors = [...new Map(scoped.map((u) => [floorInfo(u.sprat).label, { ...floorInfo(u.sprat), raw: u.sprat }])).values()]
      .filter((f) => f.label)
      .sort((a, b) => a.order - b.order);
    return { types, struct, floors };
  }, [scoped]);

  const visible = useMemo(() => {
    const sort = SORTS.find((s) => s.value === filters.sort) || SORTS[0];
    const area = AREA_RANGES.find((r) => r.value === filters.povrsina);
    return scoped
      .filter((u) => filters.prodati || unitStatus(u) !== "prodat")
      .filter((u) => !filters.tip || (u.tip || "stan") === filters.tip)
      .filter((u) => !filters.struktura || String(u.struktura) === filters.struktura)
      .filter((u) => !filters.sprat || floorInfo(u.sprat).label === filters.sprat)
      .filter((u) => !area || area.test(areaNumber(u.povrsina)))
      .sort((a, b) => {
        // slobodni prvo, pa rezervisani, pa prodati
        const rank = { slobodan: 0, rezervisan: 1, prodat: 2 };
        return rank[unitStatus(a)] - rank[unitStatus(b)] || sort.fn(a, b);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scoped, params]);

  const activeCount = ["tip", "struktura", "sprat", "povrsina"].filter((k) => filters[k]).length;
  // units.data stays null until the units for the loaded projects have arrived.
  const loading = projects.loading || units.loading || !units.data;

  if (projects.data && slug && !activeProject) {
    return <Navigate to={lp("/katalog")} replace />;
  }

  // Broj u zaglavlju odgovara listi ispod (ne brojačima sa kartice lamele).
  const freeTotal = scoped.filter((u) => unitStatus(u) === "slobodan" && (u.tip || "stan") === "stan").length;

  const seoPath = activeProject ? `/katalog/${activeProject.slug}` : "/katalog";
  const seoVars = activeProject ? { identifier: activeProject.identifier, address: activeProject.adresa } : {};
  const jsonLd = [
    breadcrumbs(
      [
        { name: t("seo.breadcrumbHome"), path: "/" },
        { name: t("seo.breadcrumbUnits"), path: "/katalog" },
        ...(activeProject ? [{ name: activeProject.identifier, path: seoPath }] : []),
      ],
      lang
    ),
    ...(activeProject
      ? [apartmentComplex(activeProject, { lang, description: field(activeProject, "informacije_o_zgradi"), free: availability(activeProject).freeFlats })]
      : []),
    ...(!loading ? [unitList(scoped.filter((u) => unitStatus(u) === "slobodan"), { lang, describe: (u) => f.unitSummary(u, projectById[u.projectId]) })] : []),
  ];

  return (
    <>
      <Seo
        title={t(activeProject ? "seo.catalogueProject.title" : "seo.catalogue.title", seoVars)}
        description={t(activeProject ? "seo.catalogueProject.description" : "seo.catalogue.description", seoVars)}
        path={seoPath}
        jsonLd={jsonLd}
        ready={!loading}
      />
      <a href="#sadrzaj" className="skip-link">
        {t("common.skip")}
      </a>
      <Navbar />
      <main id="sadrzaj" className="catalogue">
        <header className="catalogue-hero">
          <div className="container">
            <nav className="breadcrumbs" aria-label={t("catalogue.breadcrumb")}>
              <Link to={lp("/")}>{t("common.home")}</Link> <span aria-hidden="true">/</span>{" "}
              {activeProject ? <Link to={lp("/katalog")}>{t("nav.units")}</Link> : <span>{t("nav.units")}</span>}
              {activeProject && (
                <>
                  {" "}
                  <span aria-hidden="true">/</span> <span>{activeProject.identifier}</span>
                </>
              )}
            </nav>
            <div className="catalogue-title">
              <h1>{activeProject ? t("catalogue.titleProject", { identifier: activeProject.identifier, name: activeProject.naziv }) : t("catalogue.title")}</h1>
              <img src={blueprintGif} alt="" className="pixel catalogue-art" width="48" height="48" />
            </div>
            <p className="catalogue-lead">
              {activeProject ? (
                <>
                  <MapPin size={18} /> {activeProject.adresa}
                </>
              ) : (
                <>
                  <MapPin size={18} /> {t("catalogue.address")}
                </>
              )}
              {!loading && freeTotal > 0 && (
                <span className="catalogue-free">{t("project.freeFlats", { count: freeTotal })}</span>
              )}
            </p>

            <div className="tabs" role="tablist" aria-label={t("catalogue.tabs")}>
              <Link to={{ pathname: lp("/katalog"), search: params.toString() }} role="tab" aria-selected={!activeProject} className={`tab ${!activeProject ? "is-active" : ""}`}>
                {t("catalogue.all")}
              </Link>
              {projects.data?.map((p) => (
                <Link
                  key={p.id}
                  to={{ pathname: lp(`/katalog/${p.slug}`), search: params.toString() }}
                  role="tab"
                  aria-selected={activeProject?.id === p.id}
                  className={`tab ${activeProject?.id === p.id ? "is-active" : ""}`}
                >
                  {p.identifier}
                </Link>
              ))}
            </div>
          </div>
        </header>

        <div className="container catalogue-layout">
          <aside className={`filters ${filtersOpen ? "is-open" : ""}`} aria-label={t("catalogue.filters")}>
            <div className="filters-head">
              <h2>{t("catalogue.filters")}</h2>
              {activeCount > 0 && (
                <button type="button" className="filters-reset" onClick={resetFilters}>
                  <RotateCcw size={14} /> {t("catalogue.reset")}
                </button>
              )}
            </div>

            {options.types.length > 1 && (
              <fieldset className="filter-group">
                <legend>{t("catalogue.type")}</legend>
                <div className="chips">
                  <button type="button" className="chip" aria-pressed={!filters.tip} onClick={() => setFilter("tip", "")}>
                    {t("catalogue.any")}
                  </button>
                  {options.types.map((type) => (
                    <button type="button" key={type} className="chip" aria-pressed={filters.tip === type} onClick={() => setFilter("tip", filters.tip === type ? "" : type)}>
                      {f.unitTypeLabel(type)}
                    </button>
                  ))}
                </div>
              </fieldset>
            )}

            {options.struct.length > 0 && (
              <fieldset className="filter-group">
                <legend>{t("catalogue.structure")}</legend>
                <div className="chips">
                  <button type="button" className="chip" aria-pressed={!filters.struktura} onClick={() => setFilter("struktura", "")}>
                    {t("catalogue.any")}
                  </button>
                  {options.struct.map((s) => (
                    <button type="button" key={s} className="chip" aria-pressed={filters.struktura === s} onClick={() => setFilter("struktura", filters.struktura === s ? "" : s)}>
                      {f.strukturaLabel(s)}
                    </button>
                  ))}
                </div>
              </fieldset>
            )}

            <div className="filter-row">
              <div className="field">
                <label htmlFor="filter-sprat">{t("catalogue.floor")}</label>
                <select id="filter-sprat" className="select" value={filters.sprat} onChange={(e) => setFilter("sprat", e.target.value)}>
                  <option value="">{t("catalogue.allFloors")}</option>
                  {options.floors.map((fl) => (
                    <option key={fl.label} value={fl.label}>
                      {f.floorLabel(fl.raw)}
                    </option>
                  ))}
                </select>
              </div>
              <div className="field">
                <label htmlFor="filter-povrsina">{t("catalogue.area")}</label>
                <select id="filter-povrsina" className="select" value={filters.povrsina} onChange={(e) => setFilter("povrsina", e.target.value)}>
                  <option value="">{t("catalogue.allAreas")}</option>
                  {AREA_RANGES.map((r) => (
                    <option key={r.value} value={r.value}>
                      {t(`catalogue.areas.${r.value}`)}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <label className="toggle">
              <input type="checkbox" checked={filters.prodati} onChange={(e) => setFilter("prodati", e.target.checked)} />
              <span>{t("catalogue.showSold")}</span>
            </label>

            <button type="button" className="btn btn--block filters-apply" onClick={() => setFiltersOpen(false)}>
              {t("catalogue.show", { count: visible.length })}
            </button>

            <div className="filters-help">
              <p>{t("catalogue.notFound")}</p>
              <CallButton className="link" source="katalog-filteri">
                {t("catalogue.callUs", { phone: SITE.phoneDisplay })}
              </CallButton>
            </div>
          </aside>

          <section className="results" aria-live="polite" aria-busy={loading}>
            <div className="results-bar">
              <p className="results-count" data-testid="results-count">
                {loading ? t("common.loading") : t("catalogue.results", { count: visible.length })}
              </p>
              <button type="button" className="btn btn--ghost btn--sm filters-toggle" onClick={() => setFiltersOpen(true)} aria-expanded={filtersOpen}>
                <SlidersHorizontal size={16} /> {t("catalogue.filters")}{activeCount ? ` (${activeCount})` : ""}
              </button>
              <label className="results-sort">
                <span className="sr-only">{t("catalogue.sort")}</span>
                <select className="select" value={filters.sort} onChange={(e) => setFilter("sort", e.target.value === "sprat" ? "" : e.target.value)}>
                  {SORTS.map((s) => (
                    <option key={s.value} value={s.value}>
                      {t(`catalogue.sorts.${s.value}`)}
                    </option>
                  ))}
                </select>
              </label>
            </div>

            {loading && (
              <div className="unit-grid">
                {[0, 1, 2, 3, 4, 5].map((i) => (
                  <div key={i} className="skeleton unit-skeleton" />
                ))}
              </div>
            )}

            {!loading && visible.length === 0 && (
              <div className="empty-state">
                <SearchX size={36} />
                <h3>{t("catalogue.emptyTitle")}</h3>
                <p>{t("catalogue.emptyText")}</p>
                <button type="button" className="btn btn--ghost" onClick={resetFilters}>
                  {t("catalogue.resetFilters")}
                </button>
              </div>
            )}

            {!loading && visible.length > 0 && (
              <div className="unit-grid">
                {visible.map((unit) => (
                  <UnitCard
                    key={`${unit.projectId}-${unit.id}`}
                    unit={unit}
                    project={activeProject ? null : projectById[unit.projectId]}
                    onInquire={setInquiryUnit}
                    onOpenGallery={(u) => setGallery({ images: u.imageUrls, title: f.unitSummary(u, projectById[u.projectId]) })}
                  />
                ))}
              </div>
            )}

            {!loading && activeProject?.najniza_cena && (
              <p className="results-note">
                {t("catalogue.priceNote", {
                  identifier: activeProject.identifier,
                  from: activeProject.najniza_cena,
                  to: activeProject.najvisa_cena ? t("catalogue.priceTo", { price: activeProject.najvisa_cena }) : "",
                })}
              </p>
            )}
          </section>
        </div>
      </main>
      <Footer />
      <MobileActionBar onInquiry={() => setInquiryUnit({})} />

      {filtersOpen && <div className="filters-backdrop" onClick={() => setFiltersOpen(false)} />}

      {inquiryUnit && (
        <Modal title={inquiryUnit.broj ? t("catalogue.modalUnit") : t("catalogue.modalGeneral")} onClose={() => setInquiryUnit(null)}>
          <InquiryForm
            source="katalog"
            idPrefix="katalog"
            unit={inquiryUnit.broj ? unitSummary(inquiryUnit, projectById[inquiryUnit.projectId]) : undefined}
            unitLabel={inquiryUnit.broj ? f.unitSummary(inquiryUnit, projectById[inquiryUnit.projectId]) : undefined}
          />
        </Modal>
      )}
      {gallery && <Lightbox images={gallery.images} title={gallery.title} onClose={() => setGallery(null)} />}
    </>
  );
}
